// utils/fixEncoding.js
import iconv from 'iconv-lite';

/**
 * Checks if a string looks like it's mis-encoded UTF-8 (commonly read as Latin1)
 * @param {string} str
 * @returns {boolean}
 */
function isLikelyMisencoded(str) {
  if (typeof str !== 'string') return false;
  // common patterns for mis-encoded UTF-8
  return /Ã[^\s]/.test(str);
}

/**
 * Fixes a mis-encoded string if detected
 * Leaves normal strings or numbers untouched
 * @param {string | number} str
 * @returns {string | number}
 */
export function fixEncoding(str) {
  if (typeof str !== 'string') return str; // skip numbers, objects, etc.
  if (!isLikelyMisencoded(str)) return str;

  try {
    const buffer = Buffer.from(str, 'binary'); // interpret bytes as binary
    return iconv.decode(buffer, 'utf8'); // decode as UTF-8
  } catch {
    return str; // fallback, leave it as-is if decoding fails
  }
}

/**
 * Recursively fixes strings in an object or array
 * @param {any} data
 * @returns {any}
 */
export function fixEncodingDeep(data) {
  if (Array.isArray(data)) return data.map(fixEncodingDeep);
  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, fixEncodingDeep(v)])
    );
  }
  return fixEncoding(data);
}
/**
 * Fixes malformed string encodings (like "Ã" artifacts) in nested data.
 * Traverses objects/arrays iteratively (no recursion) for large dataset safety.
 *
 * @param {*} data - Any object, array, or value to fix.
 * @returns {*} The same data with corrected strings.
 */
export function fixEncodingIterative(data) {
  const stack = [{ parent: null, key: null, value: data }];

  while (stack.length > 0) {
    const { parent, key, value } = stack.pop();

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        stack.push({ parent: value, key: i, value: value[i] });
      }
    } else if (value && typeof value === 'object' && !(value instanceof Date)) {
      for (const k in value) {
        if (Object.prototype.hasOwnProperty.call(value, k)) {
          stack.push({ parent: value, key: k, value: value[k] });
        }
      }
    } else if (typeof value === 'string') {
      const fixed = /Ã/.test(value)
        ? Buffer.from(value, 'binary').toString('utf8')
        : value;

      if (parent) parent[key] = fixed;
    }
    // numbers, booleans, null, undefined, dates → ignore
  }

  return data;
}
